// AVIV Map Configuration - adapted from price-pages franceGeoConfig.ts and zoomRanges.ts

// MAP_MIN_ZOOM = 3 matches legacy www-flask (price-pages uses 4)
export const MAP_MIN_ZOOM = 3;
export const MAP_MAX_ZOOM = 22;

export const FRANCE_BOUNDARY_ID = 'AD02FR1';

// Legacy colors from www-flask map styles
export const LEGACY_SELECTED_COLOR = '#1C90FF';
export const LEGACY_HOVER_COLOR = '#18CAE6';

const PlaceType = {
    COUNTRY: 'COUNTRY',
    REGION: 'REGION',
    PROVINCE: 'PROVINCE',
    MUNICIPALITY: 'MUNICIPALITY',
    BOROUGH: 'BOROUGH',
    STATISTICAL_UNIT: 'STATISTICAL_UNIT',
    NEIGHBORHOOD: 'NEIGHBORHOOD',
    BLOCK: 'BLOCK',
    PARCEL: 'PARCEL',
    BUILDING: 'BUILDING',
};

export const TransactionType = {
    SELL: 'sell',
    RENT: 'rent',
};

// AVIV type codes <-> PlaceType mappings
export const AVIV_TYPE_TO_PLACE_TYPE = {
    AD02: PlaceType.COUNTRY,
    AD04: PlaceType.REGION,
    AD06: PlaceType.PROVINCE,
    AD08: PlaceType.MUNICIPALITY,
    AD09: PlaceType.BOROUGH,
    STU3: PlaceType.STATISTICAL_UNIT,
    BLOC: PlaceType.BLOCK,
    PARC: PlaceType.PARCEL,
    BILD: PlaceType.BUILDING,
};

export const PLACE_TYPE_TO_AVIV_TYPE = {
    [PlaceType.COUNTRY]: 'AD02',
    [PlaceType.REGION]: 'AD04',
    [PlaceType.PROVINCE]: 'AD06',
    [PlaceType.MUNICIPALITY]: 'AD08',
    [PlaceType.BOROUGH]: 'AD09',
    [PlaceType.STATISTICAL_UNIT]: 'STU3',
    [PlaceType.BLOCK]: 'BLOC',
    [PlaceType.PARCEL]: 'PARC',
    [PlaceType.BUILDING]: 'BILD',
};

// Legacy layer name <-> AVIV type code mappings
export const LEGACY_TO_AVIV_TYPE = {
    region: 'AD04',
    subregion: 'AD06',
    city: 'AD08',
    arrmun: 'AD09',
    iris: 'STU3',
    block: 'BLOC',
    parcelle: 'PARC',
    building: 'BILD',
};

export const AVIV_TYPE_TO_LEGACY = {
    AD04: 'region',
    AD06: 'subregion',
    AD08: 'city',
    AD09: 'arrmun',
    STU3: 'iris',
    BLOC: 'block',
    PARC: 'parcelle',
    BILD: 'building',
};

// France GeoConfig - configuration for each place type
// Active layers have price data and are navigable; inactive are for geo selection only
export const FRANCE_GEO_CONFIG = {
    [PlaceType.COUNTRY]: {
        active: true,
        avivType: 'AD02',
        map: {
            [TransactionType.SELL]: {
                priceLayer: { minZoom: 1 },
                selectionLayer: { minZoom: 1 },
            },
            [TransactionType.RENT]: {
                priceLayer: { minZoom: 1 },
                selectionLayer: { minZoom: 1 },
            },
            hasGeometry: true,
        },
    },
    [PlaceType.REGION]: {
        active: true,
        avivType: 'AD04',
        map: {
            [TransactionType.SELL]: {
                selectionLayer: { minZoom: 4, maxZoom: 7 },
            },
            [TransactionType.RENT]: {
                selectionLayer: { minZoom: 4, maxZoom: 7 },
            },
            hasGeometry: true,
        },
    },
    [PlaceType.PROVINCE]: {
        active: true,
        avivType: 'AD06',
        map: {
            [TransactionType.SELL]: {
                priceLayer: { minZoom: 4 },
                selectionLayer: { minZoom: 7, maxZoom: 9 },
            },
            [TransactionType.RENT]: {
                priceLayer: { minZoom: 4 },
                selectionLayer: { minZoom: 7, maxZoom: 9 },
            },
            hasGeometry: true,
        },
    },
    [PlaceType.MUNICIPALITY]: {
        active: true,
        avivType: 'AD08',
        map: {
            [TransactionType.SELL]: {
                priceLayer: { minZoom: 8 },
                selectionLayer: { minZoom: 9 },
            },
            [TransactionType.RENT]: {
                priceLayer: { minZoom: 8 },
                selectionLayer: { minZoom: 9 },
            },
            hasGeometry: true,
        },
    },
    [PlaceType.BOROUGH]: {
        active: true,
        avivType: 'AD09',
        map: {
            [TransactionType.SELL]: {
                selectionLayer: { minZoom: 9 },
            },
            [TransactionType.RENT]: {
                selectionLayer: { minZoom: 9 },
            },
            hasGeometry: true,
        },
    },
    [PlaceType.STATISTICAL_UNIT]: {
        active: false,
        avivType: 'STU3',
        map: {
            [TransactionType.SELL]: {
                priceLayer: { minZoom: 10 },
                selectionLayer: { minZoom: 10, maxZoom: 15 },
            },
            [TransactionType.RENT]: {
                priceLayer: { minZoom: 10 },
                selectionLayer: { minZoom: 10 },
            },
            hasGeometry: true,
        },
    },
    [PlaceType.BLOCK]: {
        active: false,
        avivType: 'BLOC',
        map: {
            // Blocks only used for rental, not clickable (no selectionLayer)
            [TransactionType.RENT]: {
                priceLayer: { minZoom: 13 },
            },
            hasGeometry: true,
        },
    },
    [PlaceType.PARCEL]: {
        active: false,
        avivType: 'PARC',
        map: {
            [TransactionType.SELL]: {
                priceLayer: { minZoom: 14 },
                selectionLayer: { minZoom: 15 },
            },
            hasGeometry: true,
        },
    },
    [PlaceType.BUILDING]: {
        active: false,
        avivType: 'BILD',
        map: {
            // Buildings displayed but not clickable (no selectionLayer)
            [TransactionType.SELL]: {
                priceLayer: { minZoom: 16 },
            },
            hasGeometry: true,
        },
    },
};

// "Around" selection - zoom ranges for clicking areas OUTSIDE current boundary
export const AROUND_SELECTION_ZOOM_RANGES = [
    { placeType: PlaceType.REGION, avivType: 'AD04', minzoom: 4, maxzoom: 7 },
    { placeType: PlaceType.PROVINCE, avivType: 'AD06', minzoom: 7, maxzoom: 9 },
    { placeType: PlaceType.MUNICIPALITY, avivType: 'AD08', minzoom: 9 },
    { placeType: PlaceType.BOROUGH, avivType: 'AD09', minzoom: 9 },
];

// "Within" selection - zoom ranges for clicking areas INSIDE current boundary (children)
// Each boundary level has different zoom ranges for its children
export const WITHIN_SELECTION_ZOOM_RANGES = {
    AD02: {
        [TransactionType.SELL]: [
            { avivType: 'AD04', minzoom: 4, maxzoom: 7 },
            { avivType: 'AD06', minzoom: 7, maxzoom: 9 },
            { avivType: 'AD08', minzoom: 9 },
            { avivType: 'AD09', minzoom: 9 },
        ],
        [TransactionType.RENT]: [
            { avivType: 'AD04', minzoom: 4, maxzoom: 7 },
            { avivType: 'AD06', minzoom: 7, maxzoom: 9 },
            { avivType: 'AD08', minzoom: 9 },
            { avivType: 'AD09', minzoom: 9 },
        ],
    },
    AD04: {
        [TransactionType.SELL]: [
            { avivType: 'AD06', minzoom: 3, maxzoom: 7 },
            { avivType: 'AD08', minzoom: 7 },
            { avivType: 'AD09', minzoom: 7 },
        ],
        [TransactionType.RENT]: [
            { avivType: 'AD06', minzoom: 3, maxzoom: 7 },
            { avivType: 'AD08', minzoom: 7 },
            { avivType: 'AD09', minzoom: 7 },
        ],
    },
    AD06: {
        [TransactionType.SELL]: [
            { avivType: 'AD08', minzoom: 3 },
            { avivType: 'AD09', minzoom: 3 },
        ],
        [TransactionType.RENT]: [
            { avivType: 'AD08', minzoom: 3 },
            { avivType: 'AD09', minzoom: 3 },
        ],
    },
    AD08: {
        [TransactionType.SELL]: [
            { avivType: 'STU3', minzoom: 3, maxzoom: 15 },
            { avivType: 'PARC', minzoom: 15 },
        ],
        [TransactionType.RENT]: [{ avivType: 'STU3', minzoom: 3 }],
    },
    AD09: {
        [TransactionType.SELL]: [
            { avivType: 'STU3', minzoom: 3, maxzoom: 15 },
            { avivType: 'PARC', minzoom: 15 },
        ],
        [TransactionType.RENT]: [{ avivType: 'STU3', minzoom: 3 }],
    },
};

export function getWithinSelectionZoomRanges(boundaryType, transactionType) {
    const boundaryConfig = WITHIN_SELECTION_ZOOM_RANGES[boundaryType];
    if (!boundaryConfig) {
        return WITHIN_SELECTION_ZOOM_RANGES.AD02[transactionType] || [];
    }
    return boundaryConfig[transactionType] || [];
}

// Compute zoom ranges from FRANCE_GEO_CONFIG sorted by minzoom
function computeZoomRanges(transactionType, layerType) {
    const zoomRanges = [];

    Object.entries(FRANCE_GEO_CONFIG).forEach(([placeType, config]) => {
        const mapConfig = config.map?.[transactionType];
        const layerConfig = mapConfig?.[layerType];
        const minZoom = layerConfig?.minZoom;

        if (minZoom !== undefined) {
            const range = {
                placeType,
                avivType: config.avivType,
                minzoom: minZoom,
            };
            // Include maxZoom if defined in config (for selection layers)
            if (layerConfig?.maxZoom !== undefined) {
                range.maxzoom = layerConfig.maxZoom;
            }
            zoomRanges.push(range);
        }
    });

    return zoomRanges.sort((a, b) => a.minzoom - b.minzoom);
}

// Precalculate all zoom range combinations at module load time
const ZOOM_RANGES = {
    [TransactionType.SELL]: {
        priceLayer: computeZoomRanges(TransactionType.SELL, 'priceLayer'),
        selectionLayer: computeZoomRanges(TransactionType.SELL, 'selectionLayer'),
    },
    [TransactionType.RENT]: {
        priceLayer: computeZoomRanges(TransactionType.RENT, 'priceLayer'),
        selectionLayer: computeZoomRanges(TransactionType.RENT, 'selectionLayer'),
    },
};

// Returns precalculated zoom ranges from FRANCE_GEO_CONFIG
export function getZoomRanges(transactionType, layerType = 'priceLayer') {
    return ZOOM_RANGES[transactionType]?.[layerType] ?? [];
}

// Convert PlaceType, AVIV code, or legacy name to AVIV type code
export function getAvivType(type) {
    if (/^[A-Z]+\d+$/.test(type) || type === 'BLOC' || type === 'PARC' || type === 'BILD') {
        return type;
    }

    const config = FRANCE_GEO_CONFIG[type];
    if (config?.avivType) {
        return config.avivType;
    }

    return LEGACY_TO_AVIV_TYPE[type] || null;
}

// Parse AVIV feature ID: "AD08FR75056" -> { type: "AD08", country: "FR", numericId: "75056" }
export function parseAvivFeatureId(featureId) {
    const match = featureId.match(/^(PARC|BILD|BLOC|STU3|[A-Z]+\d+)([A-Z]{2})(\d+)$/);
    if (!match) return null;

    return {
        type: match[1],
        country: match[2],
        numericId: match[3],
    };
}

// Get AVIV boundary place ID from www-flask place object
export function getAvivBoundaryFromPlace(place) {
    if (!place) {
        return FRANCE_BOUNDARY_ID;
    }

    const geotype = place.geotype_label;
    const { cog, id } = place;
    const regionId = place.region_id;
    const cityId = place.city_id;

    switch (geotype) {
        case 'country':
            return FRANCE_BOUNDARY_ID;

        case 'region':
            if (id) {
                return `AD04FR${id}`;
            }
            if (regionId) {
                return `AD04FR${regionId}`;
            }
            break;

        case 'subregion':
            if (cog) {
                return `AD06FR${cog}`;
            }
            break;

        case 'city':
        case 'arrmun':
            if (cityId) {
                return `AD08FR${cityId}`;
            }
            if (id && geotype === 'city') {
                return `AD08FR${id}`;
            }
            if (cog) {
                return `AD08FR${cog}`;
            }
            break;

        // Sub-city types use city_id for price layer context
        case 'address':
        case 'street':
        case 'borough':
        case 'subcity':
        case 'iris':
            if (cityId) {
                return `AD08FR${cityId}`;
            }
            break;

        default:
            break;
    }

    return FRANCE_BOUNDARY_ID;
}

export default {
    MAP_MIN_ZOOM,
    MAP_MAX_ZOOM,
    FRANCE_BOUNDARY_ID,
    PlaceType,
    TransactionType,
    AVIV_TYPE_TO_PLACE_TYPE,
    PLACE_TYPE_TO_AVIV_TYPE,
    LEGACY_TO_AVIV_TYPE,
    AVIV_TYPE_TO_LEGACY,
    FRANCE_GEO_CONFIG,
    AROUND_SELECTION_ZOOM_RANGES,
    WITHIN_SELECTION_ZOOM_RANGES,
    getZoomRanges,
    getWithinSelectionZoomRanges,
    getAvivType,
    parseAvivFeatureId,
    getAvivBoundaryFromPlace,
};
